import React, { useState, useEffect } from 'react';
import axios from 'axios';
import API_ENDPOINTS from '../ApiEndPoints';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

const CreateTaskModal = ({ onClose, onCreated, parentId }) => {
    const [mode, setMode] = useState('simple'); // 'simple' or 'structured'
    const [loading, setLoading] = useState(false);
    const [definitions, setDefinitions] = useState([]);
    const [definitionsLoading, setDefinitionsLoading] = useState(false);
    const [definitionsError, setDefinitionsError] = useState(null);
    const [error, setError] = useState('');

    const [formData, setFormData] = useState({
        title: '',
        description: '',
        date_due: '',
        task_def_id: '',
        parent_id: parentId || ''
    });

    const structuredUnavailable = mode === 'structured' && (definitionsLoading || definitions.length === 0);

    useEffect(() => {
        if (mode === 'structured') {
            fetchDefinitions();
        }
    }, [mode]);

    useEffect(() => {
        setFormData(prev => ({ ...prev, parent_id: parentId || '' }));
    }, [parentId]);

    const fetchDefinitions = async () => {
        try {
            setDefinitionsLoading(true);
            setDefinitionsError(null);
            const response = await axios.get(API_ENDPOINTS.tasks.definitions, {
                params: { manual_only: true }
            });
            setDefinitions(response.data.data || []);
        } catch (error) {
            console.error("Error fetching definitions:", error);
            setDefinitions([]);
            setDefinitionsError('Unable to load manual task definitions for this template.');
        } finally {
            setDefinitionsLoading(false);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');
        try {
            await axios.post(API_ENDPOINTS.tasks.store, formData);
            onCreated();
        } catch (error) {
            console.error('Failed to create task', error);
            setError(error?.response?.data?.error || 'Failed to create task.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="modal show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }} tabIndex="-1" role="dialog">
            <div className="modal-dialog" role="document">
                <div className="modal-content">
                    <div className="modal-header">
                        <button type="button" className="close" onClick={onClose} aria-label="Close">
                            <span aria-hidden="true">&times;</span>
                        </button>
                        <h4 className="modal-title">Create New Task</h4>
                    </div>
                    <div className="modal-body">
                        {error && (
                            <div className="alert alert-danger">{error}</div>
                        )}
                        <div className="btn-group btn-group-justified" role="group" style={{ marginBottom: '20px' }}>
                            <div className="btn-group" role="group">
                                <button
                                    type="button"
                                    className={`btn ${mode === 'simple' ? 'btn-primary' : 'btn-default'}`}
                                    onClick={() => setMode('simple')}
                                >
                                    Simple Task
                                </button>
                            </div>
                            <div className="btn-group" role="group">
                                <button
                                    type="button"
                                    className={`btn ${mode === 'structured' ? 'btn-primary' : 'btn-default'}`}
                                    onClick={() => setMode('structured')}
                                >
                                    Scheduled Task
                                </button>
                            </div>
                        </div>

                        <form onSubmit={handleSubmit}>
                            {mode === 'structured' && (
                                <div className="form-group">
                                    <label className="control-label">Task Definition</label>
                                    <select
                                        name="task_def_id"
                                        required
                                        value={formData.task_def_id}
                                        onChange={handleChange}
                                        className="form-control"
                                        disabled={definitionsLoading || definitions.length === 0}
                                    >
                                        {definitionsLoading && (
                                            <option value="">Loading definitions...</option>
                                        )}
                                        {!definitionsLoading && definitions.length === 0 && (
                                            <option value="">No manual task definitions available</option>
                                        )}
                                        {!definitionsLoading && definitions.length > 0 && (
                                            <>
                                                <option value="">Select a definition...</option>
                                                {definitions.map(def => (
                                                    <option key={def.id} value={def.id}>
                                                        {def.unit} - {def.role} - {def.title}
                                                    </option>
                                                ))}
                                            </>
                                        )}
                                    </select>
                                    {definitionsError && (
                                        <div className="alert alert-danger" style={{ marginTop: '10px' }}>
                                            {definitionsError}
                                        </div>
                                    )}
                                </div>
                            )}

                            <div className="form-group">
                                <label className="control-label">Title</label>
                                <input
                                    type="text"
                                    name="title"
                                    required={mode === 'simple'}
                                    value={formData.title}
                                    onChange={handleChange}
                                    placeholder={mode === 'structured' ? "Override title (optional)" : "Task title"}
                                    className="form-control"
                                />
                            </div>

                            <div className="form-group">
                                <label className="control-label">Description</label>
                                <ReactQuill
                                    theme="snow"
                                    value={formData.description}
                                    onChange={(value) => setFormData(prev => ({ ...prev, description: value }))}
                                    placeholder={mode === 'structured' ? "Override description (optional)" : "Task description"}
                                    style={{ height: '200px', marginBottom: '50px' }}
                                />
                            </div>

                            <div className="form-group">
                                <label className="control-label">Due Date</label>
                                <input
                                    type="date"
                                    name="date_due"
                                    value={formData.date_due}
                                    onChange={handleChange}
                                    className="form-control"
                                />
                            </div>
                        </form>
                    </div>
                    <div className="modal-footer">
                        <button type="button" className="btn btn-default" onClick={onClose}>Cancel</button>
                        <button
                            type="button"
                            onClick={handleSubmit}
                            disabled={loading || structuredUnavailable}
                            className="btn btn-primary"
                        >
                            {loading ? 'Creating...' : 'Create'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CreateTaskModal;
